import { useEffect, useRef } from 'react';
import { toast } from 'sonner';

/**
 * useIdleLogout
 * -------------
 * Triggers `onLogout()` after `timeoutMs` of inactivity. Shows a 1-minute
 * warning toast (and an audible browser confirm beep) before forced logout
 * so users can recover.
 *
 * "Activity" = mouse move, click, keypress, scroll, touch, or any axios
 * request firing (covers background data loads).
 *
 * Storage: last-activity timestamp is mirrored into localStorage so a single
 * tab burst of activity refreshes other tabs of the same user (no pre-mature
 * logout when they have multiple tabs open).
 */
export function useIdleLogout(enabled, onLogout, {
  timeoutMs = 30 * 60 * 1000, // 30 min default
  warnBeforeMs = 60 * 1000,   // warn 60s before logout
  storageKey = 'jv_last_activity',
  checkIntervalMs = 15 * 1000, // re-check every 15s
} = {}) {
  const warnedRef = useRef(false);
  const checkTimerRef = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;

    const stamp = () => {
      const now = Date.now();
      try { localStorage.setItem(storageKey, String(now)); } catch (e) { /* ignore quota errors */ }
      warnedRef.current = false;
    };

    // Initial stamp on mount
    stamp();

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    events.forEach(evt => window.addEventListener(evt, stamp, { passive: true }));

    const tick = () => {
      let last = Date.now();
      try {
        const stored = parseInt(localStorage.getItem(storageKey) || '0', 10);
        if (stored) last = stored;
      } catch (e) { /* ignore */ }
      const idle = Date.now() - last;

      if (idle >= timeoutMs) {
        toast.error('You have been logged out due to 30 minutes of inactivity.', { duration: 6000 });
        onLogout();
        return;
      }
      if (!warnedRef.current && idle >= timeoutMs - warnBeforeMs) {
        warnedRef.current = true;
        toast.warning('You will be logged out in 1 minute due to inactivity. Move your mouse or press a key to stay signed in.', { duration: warnBeforeMs });
      }
    };

    checkTimerRef.current = setInterval(tick, checkIntervalMs);

    return () => {
      events.forEach(evt => window.removeEventListener(evt, stamp));
      if (checkTimerRef.current) clearInterval(checkTimerRef.current);
    };
  }, [enabled, onLogout, timeoutMs, warnBeforeMs, storageKey, checkIntervalMs]);
}
